/**
 * Pure shortfall math and copy for credit-blocked design pages.
 *
 * Kept free of runtime imports so both the components and their tests can use
 * it without dragging in the API client; `useDesignCreditStatus` supplies the
 * live data.
 */
import type { DesignCreditStatus, DesignPage } from '../services/designService';

import { formatPhaseCredits } from './phaseBuildPresentation';

const positiveNumber = (value: number | null | undefined): number | undefined => (
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
);

/** Credits the user must add before the blocked pages can be designed. */
export const designCreditShortfall = (
  status: DesignCreditStatus | null | undefined,
): number | undefined => positiveNumber(status?.shortfall_credits);

/**
 * Credits needed for one page. Falls back to the project shortfall so a page
 * whose plan was never saved still names a number the user can act on.
 */
export const pageCreditShortfall = (
  page: Pick<DesignPage, 'credit_required'> | null | undefined,
  status: DesignCreditStatus | null | undefined,
): number | undefined => {
  const required = positiveNumber(page?.credit_required);

  if (required === undefined) return designCreditShortfall(status);
  const available = Math.max(0, status?.available_credits ?? 0);

  return positiveNumber(required - available);
};

/** One-line "N more credits needed (X available of Y required)" copy. */
export const designCreditCopy = (
  status: DesignCreditStatus | null | undefined,
): string | null => {
  const shortfall = designCreditShortfall(status);

  if (shortfall === undefined || !status) return null;

  return `${formatPhaseCredits(shortfall)} more credits needed `
    + `(${formatPhaseCredits(status.available_credits)} available of `
    + `${formatPhaseCredits(status.required_credits)} required).`;
};

/** Hint copy for one credit-blocked page's empty state. */
export const pageCreditCopy = (
  page: Pick<DesignPage, 'credit_required'> | null | undefined,
  status: DesignCreditStatus | null | undefined,
): string => {
  const shortfall = pageCreditShortfall(page, status);

  if (shortfall === undefined) return 'Add credits, then continue to generate this page.';

  const amount = `Add ${formatPhaseCredits(shortfall)} more credits to generate this page`;
  const required = positiveNumber(page?.credit_required);

  if (!status || required === undefined) return `${amount}.`;

  return `${amount} (${formatPhaseCredits(status.available_credits)} available `
    + `of ${formatPhaseCredits(required)} required).`;
};
